/**
 * SOLID Review
 * S — Strong. Sole job: own the graphql-ws socket's health — connection-state tracking, the zombie-socket
 *     keep-alive ping/ack detector, and foreground-resume recovery wired to the five browser events that
 *     signal "the player's attention came back" on mobile. Extracted as Part 3 of this pass —
 *     apolloClient.ts no longer needs to know any of this exists beyond importing `wsClient`.
 * O — Good. a new "return from away" signal is a new `window`/`document` listener calling
 *     `reconnectIfReturned`, not a change to the detection thresholds or the ApolloClient wiring.
 * L — N/A — no inheritance hierarchy; this is module-level setup code, not a class.
 * I — Good. Exposes exactly the surface consumers need (`wsClient`, `forceReconnect`,
 *     `getWsConnectionState`, `onWsConnectionStateChange`, the `WsConnectionState` type) — nothing about
 *     HTTP transport or token storage leaks in here.
 * D — Good. Depends on `sessionTokenStorage.ts` (for `connectionParams`) — a same-layer dependency, not a
 *     reach into Application state. Independently computes WS_URL (small, ~8-line duplication of
 *     apolloClient.ts's HTTP_URL resolution) rather than adding a 4th shared-config file for one constant
 *     each — judged simpler than the alternative.
 */
import { createClient } from 'graphql-ws';
import { PROD_GRAPHQL_WS_URL } from '../../shared/appConfig';
import { getSessionToken, getResumeSession } from '../storage/sessionTokenStorage';

/**
 * Choose the WebSocket endpoint: an explicit override, else a same-origin dev URL, else
 * production.
 *
 * Exported and parameterised rather than computed inline from `import.meta.env` so it can be
 * tested. As inline module-level ternaries these branches were unreachable from a test (a stub
 * of `import.meta.env` does not reach another module's captured reference), which left the one
 * decision that can point every player's socket at the wrong host as the one decision nothing
 * checked. Extracted 2026-08-16.
 */
export function resolveWsUrl(
  env: Record<string, unknown>,
  location: { protocol: string; host: string },
): string {
  const explicit = env['VITE_GRAPHQL_WS_URL'];
  if (typeof explicit === 'string' && explicit !== '') return explicit;
  const isDev = env['DEV'] === 'true' || env['DEV'] === true;
  if (!isDev) return PROD_GRAPHQL_WS_URL;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/graphql`;
}

const WS_URL = resolveWsUrl(
  /* c8 ignore next -- the `{}` arm needs a loader with no import.meta.env; under Vite/vitest the
     object always exists, so this is unreachable in the test environment by construction. The
     DECISION it feeds is covered exhaustively by resolveWsUrl's own tests. */
  (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {},
  window.location,
);

// ---------------------------------------------------------------------------
// Connection state tracking
// ---------------------------------------------------------------------------

export type WsConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'failed';

let _wsState: WsConnectionState = 'disconnected';
const _wsListeners = new Set<(s: WsConnectionState) => void>();

function setWsState(s: WsConnectionState): void {
  if (_wsState === s) return;
  _wsState = s;
  _wsListeners.forEach((fn) => fn(s));
}

export function getWsConnectionState(): WsConnectionState { return _wsState; }

export function onWsConnectionStateChange(
  handler: (s: WsConnectionState) => void
): () => void {
  _wsListeners.add(handler);
  return () => _wsListeners.delete(handler);
}

// ---------------------------------------------------------------------------
// WebSocket client (graphql-ws protocol) with lazy auth header injection,
// dead-socket detection (keep-alive ping/pong) and foreground-resume recovery.
//
// Mobile context: when a player switches to another app the OS freezes this
// tab's JS/timers and often suspends the TCP socket. The socket can be left
// "half-open" — the server already dropped its side (5s ws heartbeat) but the
// browser still reports the socket as OPEN, so graphql-ws keeps `_wsState` at
// 'connected' and silently delivers no game events until a TCP timeout (which
// can be minutes). Two mechanisms below collapse that window:
//   1. keepAlive ping + ack timeout → close(4408): steady-state zombie detector
//      (worst case ~15s), per the graphql-ws docs recipe.
//   2. return-from-away → forceReconnect(): when the player's attention comes
//      back, abruptly terminate the (possibly dead) socket so graphql-ws
//      reconnects immediately rather than waiting for #1 or a TCP timeout. The
//      reconnect re-sends playerId/roomId, and the backend onConnect cancels the
//      disconnect grace timer (or resumes the player if already suspended).
//      "Return" is detected across ALL the ways it happens on mobile — see the
//      listener block below — not just a full app-switch. Android split-screen
//      in particular keeps BOTH panes at visibilityState 'visible', so moving
//      focus between them fires `focus`/`blur` but NOT `visibilitychange`.
// ---------------------------------------------------------------------------

// Captured live socket + outstanding ping-ack timer, used by the keep-alive
// zombie detector below.
let _activeSocket: WebSocket | null = null;
/**
 * Who the OPEN socket introduced itself as (`playerId:roomId`), and who the one being opened will — '' for an
 * anonymous socket. The server learns the seat ONLY from `connection_init` (GraphQLTransport.onWsConnect); see
 * `reidentify` below for why that has to be kept true.
 */
let _identity = '';
let _pendingIdentity = '';
const identityOf = (session: { readonly playerId: string; readonly roomId: string } | null): string =>
  (session === null ? '' : `${session.playerId}:${session.roomId}`);
let _pingAckTimeout: ReturnType<typeof setTimeout> | null = null;
const PING_ACK_TIMEOUT_MS = 5_000;

export const wsClient = createClient({
  url: WS_URL,
  lazy: true,
  connectionParams: () => {
    const token = getSessionToken();
    const session = getResumeSession();
    _pendingIdentity = identityOf(session);
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(session ? { playerId: session.playerId, roomId: session.roomId } : {}),
    };
  },
  // Send a client ping every 10s; if the server doesn't pong within 5s, the
  // socket is dead — close it so the retry logic below reconnects.
  keepAlive: 10_000,
  // Retry forever (a mobile network can flap for a while). Without this the
  // client would give up after the default attempts and need a manual refresh.
  retryAttempts: Infinity,
  shouldRetry: () => true,
  // Capped exponential backoff so a long outage doesn't hammer the server.
  retryWait: async (retries: number) => {
    const delay = Math.min(1_000 * 2 ** retries, 30_000);
    await new Promise<void>((resolve) => setTimeout(resolve, delay));
  },
  on: {
    connecting: () => setWsState(_wsState === 'disconnected' ? 'connecting' : 'reconnecting'),
    connected: (socket) => {
      _activeSocket = socket as WebSocket;
      _identity = _pendingIdentity;
      setWsState('connected');
      // A seat saved while this socket was being opened (after its params were read) is announced now.
      reidentify();
    },
    // `received === false` ⇒ we just SENT a keep-alive ping; arm the ack timer.
    ping: (received) => {
      if (!received) {
        _pingAckTimeout = setTimeout(() => {
          if (_activeSocket && _activeSocket.readyState === WebSocket.OPEN) {
            _activeSocket.close(4408, 'Request Timeout');
          }
        }, PING_ACK_TIMEOUT_MS);
      }
    },
    // `received === true` ⇒ server answered our ping; the socket is alive.
    pong: (received) => {
      if (received && _pingAckTimeout !== null) {
        clearTimeout(_pingAckTimeout);
        _pingAckTimeout = null;
      }
    },
    closed: () => {
      _activeSocket = null;
      if (_pingAckTimeout !== null) {
        clearTimeout(_pingAckTimeout);
        _pingAckTimeout = null;
      }
      setWsState('disconnected');
    },
    error: () => setWsState('failed'),
  },
});

/**
 * Abruptly drop the current WebSocket so graphql-ws reconnects on the next tick.
 * Safe to call when idle (lazy client with no active subscriptions stays closed)
 * and when already connected (forces a fresh, immediately-reconnecting socket).
 * Used on foreground-resume / network-restore to recover a half-open socket
 * without waiting for the keep-alive ack timeout or a TCP-level timeout.
 */
export function forceReconnect(): void {
  try {
    wsClient.terminate();
  } catch {
    /* terminate on an already-dead/never-opened socket is a no-op */
  }
}

/**
 * RE-INTRODUCE THE SOCKET AS THE SEATED PLAYER (2026-10-07, frontend strict-ports FE9, §28.206).
 *
 * The server tracks a player's presence — the disconnect grace that suspends a player who has gone, and the
 * resume when they come back — ONLY for a socket whose `connection_init` named `playerId` and `roomId`; any
 * other socket is anonymous to it for its whole life. The client is lazy: the socket opens with the first
 * subscription and reads its params then. Since the server started pushing the account's balance (backend
 * §28.190, frontend commit 221a9ff6, 2026-10-01), the first subscription is the player's own balance, which
 * starts as soon as there is a player — on the welcome screen, BEFORE any seat is stored. So every socket
 * opened anonymous and stayed so: a player who dropped out was never given a grace period, only the slower
 * inactivity sweep, and one who came back was never resumed by the reconnect. The e2e cells DEV-KILL-DECIDING-C,
 * SUS-DEC-POISON-PRECLAIM, SUS-ROTATE-to-suspended and TMR-GRACE-DECIDING-C turned red on that (staging's
 * bundle took it in on 2026-10-02); production has had it since that frontend deploy.
 *
 * So when the tab stores a seat (`ClientTabStorage.persist`), the socket is re-opened IF it introduced itself
 * as anyone else — graphql-ws re-reads the params and re-subscribes every stream. A seat equal to the
 * socket's own reconnects nothing; a cleared seat (leaving) reconnects nothing either, as before.
 */
export function reidentify(): void {
  if (_activeSocket === null) return;
  const wanted = identityOf(getResumeSession());
  if (wanted === '' || wanted === _identity) return;
  forceReconnect();
}

// ---------------------------------------------------------------------------
// Return-from-away recovery (browser only).
//
// Reconnect a possibly-frozen socket whenever the player's attention returns,
// across every way that can happen on mobile:
//   - full app-switch          → document `visibilitychange` (hidden→visible)
//   - Android split-screen /
//     multi-window focus move   → window `focus`/`blur` — both panes stay
//                                 visibilityState 'visible', so visibilitychange
//                                 never fires; only focus does. THIS is the case
//                                 the first cut missed (real Galaxy A35 report).
//   - back/forward (bfcache)    → window `pageshow`
//   - network restore           → window `online`
//
// `focus` fires far more often than a real return (every tap back into the
// window), so we only reconnect after a meaningful absence
// (AWAY_RECONNECT_THRESHOLD_MS) — a quick blur/focus flicker leaves a healthy
// socket untouched. `pageshow`/`online` always reconnect (socket definitely
// suspect). A terminate while already connected is a sub-second, overlay-free
// reconnect (see §13.30), so the guard is about avoiding needless churn, not
// correctness.
// ---------------------------------------------------------------------------
const AWAY_RECONNECT_THRESHOLD_MS = 2_000;
// Only force-cycle a socket that is ALREADY 'connected' after a long absence — a
// short focus flick must NOT terminate a healthy connection. On a single device
// with several split-screen panes the OS already churns the sockets (freeze/wake);
// terminating healthy ones on every focus on top of that storms the backend's
// per-player disconnect-grace timers (each reconnect cancels the grace), so a
// genuinely-gone player is never stably suspended and the table stalls. A real
// half-open zombie is still caught by the keep-alive ping/ack timeout (10s+5s).
const ZOMBIE_RECONNECT_THRESHOLD_MS = 30_000;
let _awaySince = 0;

function markAway(): void {
  // Idempotent: first loss-of-attention wins, so paired events (e.g. blur AND
  // visibilitychange:hidden on a full app-switch) record a single away-start.
  if (_awaySince === 0) _awaySince = Date.now();
}

/** Reconnect only when the socket actually needs it — never churn a healthy one. */
function reconnectIfReturned(always = false): void {
  const awayMs = _awaySince === 0 ? 0 : Date.now() - _awaySince;
  _awaySince = 0;
  if (always) { forceReconnect(); return; }                       // pageshow / online
  if (_wsState !== 'connected') {
    if (awayMs >= AWAY_RECONNECT_THRESHOLD_MS) forceReconnect();   // genuinely down → kick it
    return;
  }
  if (awayMs >= ZOMBIE_RECONNECT_THRESHOLD_MS) forceReconnect();   // connected but long away → clear a possible zombie
}

/* c8 ignore next 2 -- non-browser guards. jsdom always provides both, so the false arms cannot
   be taken in a test. They stay because this module is imported by apolloClient, which a build
   or tooling script could load outside a browser; without them that throws at import time. */
if (typeof window !== 'undefined') {
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reconnectIfReturned();
      else markAway();
    });
  }
  window.addEventListener('blur', () => markAway());
  window.addEventListener('focus', () => reconnectIfReturned());
  window.addEventListener('pageshow', () => reconnectIfReturned(true));
  window.addEventListener('online', () => reconnectIfReturned(true));
}
